import moment from 'moment';

import { calculateDiscountedPrice } from '@/components/order/utils';
import type { SelectedProduct } from '@/hooks/patient/refill/useOrderMedicationController';
import { Logger } from '@/utils/logger';
import type { CreditLineItem, RefillResponsePatientDiscount } from '@montugroup/circuit-shared/types/refill';

const logger = new Logger('patientCredit.service');

export type DiscountProductDetails = Omit<SelectedProduct, 'data'> & {
  data: Pick<
    SelectedProduct['data'],
    'is_concession' | 'product_id' | 'price' | 'quantity' | 'short_name' | 'product_name'
  >;
};

// TODO: Don't re-implement this business logic on FE
function isPatientDiscountActive(patientDiscount: RefillResponsePatientDiscount | undefined) {
  if (!patientDiscount) {
    return false;
  }

  const isWithinOrderLimit = patientDiscount.order_limit !== 0 || patientDiscount.order_limit === null;
  if (!isWithinOrderLimit) {
    return false;
  }
  if (patientDiscount.is_indefinite) {
    return true;
  }
  const now = moment();
  return moment(patientDiscount.start_date).isBefore(now) && moment(patientDiscount.end_date).isAfter(now);
}

export interface OrderData {
  selectedProducts: DiscountProductDetails[];
  patientDiscounts: RefillResponsePatientDiscount[];
}

const getPatientDiscountCreditLineItems = (orderData: OrderData) => {
  let i = 0;
  const discountsAndCredits: CreditLineItem[] = [];

  orderData.selectedProducts.forEach((product) => {
    const isConcessionProduct = product.data.is_concession;

    // TODO: Don't re-implement this business logic on FE
    const patientDiscounts = orderData.patientDiscounts;
    const applicableDiscounts = patientDiscounts
      .filter((patientDiscount) => {
        if (patientDiscount.is_concession) {
          return !isConcessionProduct;
        }
        return patientDiscount.product_id === product.data.product_id;
      })
      .filter((patientDiscount) => {
        return isPatientDiscountActive(patientDiscount);
      })
      .map((patientDiscount) => {
        return {
          ...patientDiscount,
          discount_percentage:
            typeof patientDiscount.discount_percentage === 'string'
              ? parseFloat(patientDiscount.discount_percentage)
              : patientDiscount.discount_percentage,
        };
      })
      .sort((a, b) => {
        // Existing FE doesn't support discount_amount discounts
        const aValue = a.discount_percentage || 0;
        const bValue = b.discount_percentage || 0;
        return bValue - aValue; // Ascending for higher % first
      });
    const currentDiscount = applicableDiscounts[0];
    logger.debug('Evaluated product specific discount', { patientDiscounts, product, applicableDiscounts });

    if (currentDiscount && currentDiscount.discount_percentage) {
      const discountedAmount = calculateDiscountedPrice({
        price: Number(product.data.price) * product.data.quantity,
        discountPercent: currentDiscount.discount_percentage,
      });

      discountsAndCredits.push({
        amount: discountedAmount || 0,
        isDiscount: true,
        isProduct: true,
        name: `${currentDiscount.is_concession ? 'Concession' : ''} Discount ${Math.floor(
          currentDiscount.discount_percentage,
        )} % - ${product.data.short_name || product.data.product_name} `.trim(),
        quantity: '1',
        id: `discount-${(i += 1)}`,
        productId: product.data.product_id,
        displayName: `${Math.floor(
          currentDiscount.discount_percentage,
        )}% ${currentDiscount.is_concession ? 'concession' : ''} discount`.trim(), //this change is only temporary until UMED-1226 is ready
      });
    }
  });

  return discountsAndCredits;
};

export { getPatientDiscountCreditLineItems };
